import type { Cents } from "../domain/money";
import { formatCents } from "../domain/money";
import type { Baseline, ChangeOrder } from "../domain/baseline";
import type { ReportIdentity, ReportModel, ReportRow, ReportSection, ReportValue } from "./reportModel";

/**
 * BASELINE, CHANGE ORDERS AND THE AUTHORIZED BUDGET (Phase 13, spec §42D).
 *
 * Three documents, one set of frozen facts from Phase 11:
 *   • the authorized-budget HISTORY — how we got from the initial baseline
 *     to today's authorized figure;
 *   • a printable INDIVIDUAL CHANGE ORDER;
 *   • a what-if COMPARISON of the authorized budget against a candidate
 *     design, which authorizes nothing by existing.
 *
 * Pure: baselines and change orders are already immutable records, and this
 * file only arranges them.
 */

const money = (cents: Cents): ReportValue => ({ kind: "money", cents });
const text = (value: string): ReportValue => ({ kind: "text", text: value });

export interface BudgetHistoryInput {
  identity: ReportIdentity;
  baselines: Baseline[];
  changeOrders: ChangeOrder[];
  authorizedCents: Cents;
  remainingContingencyCents: Cents;
  currentWorkingCents: Cents;
  generatedAt: string;
}

export function baselineChangeOrderReport(input: BudgetHistoryInput): ReportModel {
  const sections: ReportSection[] = [];
  const current = input.baselines.find((b) => b.supersededAt === null) ?? input.baselines[0] ?? null;

  if (!current) {
    sections.push({
      title: "No baseline",
      columnLabel: "",
      columns: ["Amount"],
      rows: [
        {
          label: "Nothing has been approved yet",
          note: "Without a baseline there is no authorized budget — only a working estimate.",
          values: [{ kind: "empty" }],
        },
      ],
    });
  } else {
    const rows: ReportRow[] = [
      { label: "Baseline", values: [text(current.name)] },
      { label: "Approved", values: [text(current.approvedAt.slice(0, 10))] },
      { label: "Approved design", values: [text(current.designName || "—")] },
      { label: "Scope frozen", values: [money(current.scopeCents)] },
      { label: "Contingency frozen", values: [money(current.contingencyCents)] },
      { label: "Baseline total", values: [money(current.totalCents)], emphasis: "subtotal" },
      {
        label: "Frozen assumptions",
        note: `Labor ${formatCents(current.laborRateCentsPerHour)}/hour · estimating tax ${
          current.estimatingTaxPctX100 === null ? "none" : `${(current.estimatingTaxPctX100 / 100).toFixed(2)}%`
        }`,
        values: [{ kind: "empty" }],
      },
      { label: "Scope lines frozen", values: [{ kind: "count", value: current.lines.length }] },
    ];
    const superseded = input.baselines.filter((b) => b.supersededAt !== null);
    for (const old of superseded) {
      rows.push({
        label: `Superseded: ${old.name}`,
        note: `${old.supersededAt?.slice(0, 10) ?? ""} — ${old.supersededReason || "corrected"}. Kept as history.`,
        values: [money(old.totalCents)],
      });
    }
    sections.push({ title: "Initial baseline", columnLabel: "", columns: ["Amount"], rows });
  }

  const approved = input.changeOrders.filter((c) => c.status === "approved");
  const drafts = input.changeOrders.filter((c) => c.status === "draft");
  const cancelled = input.changeOrders.filter((c) => c.status === "cancelled");

  for (const order of input.changeOrders) {
    const rows: ReportRow[] = [
      {
        label: order.title,
        note: `${statusLabel(order)} · ${order.fundingSource === "contingency" ? "Contingency funded" : "Budget funded"}${
          order.designName ? ` · ${order.designName}` : ""
        }`,
        values: [money(order.netDeltaCents)],
        emphasis: "subtotal",
        source: { kind: "changeOrder", id: order.id },
      },
      ...order.lines.map(
        (line): ReportRow => ({
          label: line.description,
          note: [line.roomName, line.featureName, changeKindLabel(line.kind)].filter(Boolean).join(" · ") || undefined,
          depth: 1,
          values: [money(line.deltaCents)],
        }),
      ),
    ];
    if (order.reason) {
      rows.push({ label: "Reason", note: order.reason, depth: 1, values: [{ kind: "empty" }] });
    }
    sections.push({ title: `${order.number} — ${order.title}`, columnLabel: "Change", columns: ["Delta"], rows });
  }

  const budgetFunded = approved
    .filter((c) => c.fundingSource !== "contingency")
    .reduce((sum, c) => sum + c.netDeltaCents - c.contingencyCents, 0);
  const contingencyFunded = approved.reduce(
    (sum, c) => sum + (c.fundingSource === "contingency" ? c.netDeltaCents : c.contingencyCents),
    0,
  );

  const summaryRows: ReportRow[] = [
    { label: "Initial baseline", values: [money(current?.totalCents ?? 0)] },
    { label: "Approved change orders (budget funded)", values: [money(budgetFunded)] },
    {
      label: "Approved change orders (contingency funded)",
      note: "Already inside the frozen contingency, so the authorized total does not move.",
      values: [money(contingencyFunded)],
    },
    { label: "Authorized budget", values: [money(input.authorizedCents)], emphasis: "total" },
    { label: "Remaining contingency", values: [money(input.remainingContingencyCents)] },
    { label: "Calculated scope + contingency", values: [money(input.currentWorkingCents)] },
    {
      label: "Unapproved planning difference",
      note: "Working estimate beyond what has been authorized — a planning question, not an overrun.",
      values: [money(Math.max(0, input.currentWorkingCents - input.authorizedCents))],
    },
  ];
  if (drafts.length > 0) {
    summaryRows.push({
      label: `${drafts.length} draft change order${drafts.length === 1 ? "" : "s"}`,
      note: "Drafts authorize nothing.",
      values: [money(drafts.reduce((sum, c) => sum + c.netDeltaCents, 0))],
      emphasis: "warning",
    });
  }
  sections.push({ title: "Authorized budget", columnLabel: "", columns: ["Amount"], rows: summaryRows });

  const warnings: string[] = [];
  if (drafts.length > 0) warnings.push(`${drafts.length} change order(s) are still drafts and authorize nothing.`);
  if (cancelled.length > 0) warnings.push(`${cancelled.length} change order(s) were cancelled; they are listed for the record.`);

  return {
    id: "baseline-change-orders",
    title: "Baseline & change orders",
    subtitle: current ? `${current.name} → authorized ${formatCents(input.authorizedCents)}` : "No baseline approved",
    identity: input.identity,
    sections,
    warnings,
    disclosures: [
      "A baseline is frozen when it is approved: later scope edits, price changes and design switches never move it.",
      "Change orders are the only way the authorized budget moves. Approved orders are never edited — a correction is a new order, and negative change orders reduce the budget.",
    ],
    generatedAt: input.generatedAt,
  };
}

/* ------------------------------------------------------------------ */
/* One printable change order                                          */
/* ------------------------------------------------------------------ */

export function changeOrderDocument(input: {
  identity: ReportIdentity;
  order: ChangeOrder;
  baselineName: string;
  authorizedBeforeCents: Cents;
  authorizedAfterCents: Cents;
  generatedAt: string;
}): ReportModel {
  const { order } = input;
  const detailRows: ReportRow[] = order.lines.map((line) => ({
    label: line.description,
    note: [line.roomName, line.featureName, line.productName, changeKindLabel(line.kind)].filter(Boolean).join(" · ") || undefined,
    values: [money(line.baselineAmountCents), money(line.currentAmountCents), money(line.deltaCents)],
  }));
  detailRows.push({
    label: "Net change",
    values: [{ kind: "empty" }, { kind: "empty" }, money(order.netDeltaCents)],
    emphasis: "total",
  });

  const headerRows: ReportRow[] = [
    { label: "Change order", values: [text(order.number)] },
    { label: "Title", values: [text(order.title)] },
    { label: "Status", values: [text(statusLabel(order))] },
    { label: "Raised", values: [text(order.createdAt.slice(0, 10))] },
    { label: "Approved", values: [text(order.approvedAt ? order.approvedAt.slice(0, 10) : "—")] },
    { label: "Against baseline", values: [text(input.baselineName)] },
    { label: "Design", values: [text(order.designName || "—")] },
    {
      label: "Funding source",
      note:
        order.fundingSource === "contingency"
          ? "Drawn from the frozen contingency — the authorized total does not move."
          : "Budget funded — this raises the authorized budget.",
      values: [text(order.fundingSource === "contingency" ? "Contingency" : "Budget")],
    },
  ];

  const effectRows: ReportRow[] = [
    { label: "Authorized budget before", values: [money(input.authorizedBeforeCents)] },
    { label: "This change order", values: [money(order.netDeltaCents)] },
    { label: "Authorized budget after", values: [money(input.authorizedAfterCents)], emphasis: "total" },
  ];

  const sections: ReportSection[] = [
    { title: "Change order", columnLabel: "", columns: ["Detail"], rows: headerRows },
    { title: "Itemized changes", columnLabel: "Item", columns: ["Previous", "New", "Delta"], rows: detailRows },
    { title: "Effect on the authorized budget", columnLabel: "", columns: ["Amount"], rows: effectRows },
  ];
  if (order.reason || order.notes) {
    sections.push({
      title: "Reason & notes",
      columnLabel: "",
      columns: [],
      rows: [
        ...(order.reason ? [{ label: "Reason", note: order.reason, values: [] }] : []),
        ...(order.notes ? [{ label: "Notes", note: order.notes, values: [] }] : []),
      ],
    });
  }
  sections.push({
    title: "Approval",
    columnLabel: "",
    columns: ["Name", "Date"],
    rows: [
      { label: "Prepared by", values: [text(order.createdBy ?? "____________________"), text("____________")] },
      { label: "Approved by", values: [text(order.approvedBy ?? "____________________"), text("____________")] },
    ],
    note: "Signature lines are for a printed record; Berrier Reno stores the approval, not a signature.",
  });

  return {
    id: `change-order-${order.number}`,
    title: `Change order ${order.number}`,
    subtitle: order.title,
    identity: input.identity,
    sections,
    warnings: order.status === "draft" ? ["This change order is a DRAFT. It authorizes nothing until it is approved."] : [],
    disclosures: [
      "A change order authorizes an expectation. It never creates a transaction: actual money is recorded separately in the project's accounting.",
    ],
    generatedAt: input.generatedAt,
  };
}

/* ------------------------------------------------------------------ */
/* Authorized budget vs a candidate design                             */
/* ------------------------------------------------------------------ */

export interface DesignCandidateInput {
  identity: ReportIdentity;
  authorizedCents: Cents;
  approvedDesignName: string;
  candidateDesignName: string;
  candidateCents: Cents;
  /** Room/feature rows from the candidate design, for context. */
  rows: { label: string; note?: string; amountCents: Cents }[];
  contingencyCents: Cents;
  generatedAt: string;
}

export function baselineVsDesignReport(input: DesignCandidateInput): ReportModel {
  const delta = input.candidateCents + input.contingencyCents - input.authorizedCents;
  return {
    id: "baseline-vs-design",
    title: "Authorized budget vs candidate design",
    subtitle: `${input.approvedDesignName || "Authorized"} → ${input.candidateDesignName}`,
    identity: input.identity,
    sections: [
      {
        title: "What switching would mean",
        columnLabel: "",
        columns: ["Amount"],
        rows: [
          { label: "Authorized budget today", values: [money(input.authorizedCents)] },
          { label: `${input.candidateDesignName} scope`, values: [money(input.candidateCents)] },
          { label: "Contingency (unchanged)", values: [money(input.contingencyCents)] },
          {
            label: "Difference to authorize",
            note:
              delta === 0
                ? "The candidate design costs exactly what is already authorized."
                : delta > 0
                  ? "Switching would need a change order for this amount."
                  : "Switching would reduce the authorized budget by this amount.",
            values: [money(delta)],
            emphasis: "total",
          },
        ],
      },
      {
        title: `${input.candidateDesignName} scope`,
        columnLabel: "Scope",
        columns: ["Amount"],
        rows: input.rows.map((row) => ({ label: row.label, note: row.note, values: [money(row.amountCents)] })),
      },
    ],
    disclosures: [
      "This is ANALYSIS ONLY. Generating it approves nothing, changes no design and moves no budget — switching the approved design is a separate, explicit decision.",
      "The candidate figures are the current working estimate for that design, evaluated by the same engine as every other estimate.",
    ],
    generatedAt: input.generatedAt,
  };
}

function statusLabel(order: ChangeOrder): string {
  if (order.status === "approved") return "Approved";
  if (order.status === "cancelled") return "Cancelled";
  return "Draft";
}

function changeKindLabel(kind: string): string {
  switch (kind) {
    case "added":
      return "added scope";
    case "removed":
      return "removed scope";
    case "changed":
      return "changed";
    default:
      return "allowance";
  }
}
